import {
  CONTACT_TYPE_OPTIONS,
  CUSTOMER_TYPE_OPTIONS,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  MARITAL_STATUS_LABELS,
  PROPERTY_SUBTYPE_LABELS,
  PROPERTY_TYPE_LABELS,
  TRANSACTION_KIND_OPTIONS,
} from '../common/labels'
import { SEARCH_MIN_LENGTH, normalizePhoneSearch } from '../common/search'
import { REGION_DISTRICTS, REGION_LABELS, SLOVAK_REGIONS } from '../slovak-regions'
import { subtypesForGroups } from '../uc/categories'
import { PROPERTY_TYPES } from './property.types'

/**
 * Pokročilé filtrovanie zoznamu kontaktov — modal v admine a `GET /contacts?adv=`. Katalóg polí, operátory
 * a kontrola podmienok sú tu, aby admin aj API prijali presne to isté; SQL skladá API
 * (`crm/contacts/advanced-filter`). Návrh: docs/superpowers/specs/2026-11-01-pokrocile-filtrovanie-kontaktov-design.md
 */

export const ADVANCED_FILTER_MAX_CONDITIONS = 20
export const ADVANCED_FILTER_MAX_TEXT = 200
/**
 * Strop parametra `adv` v bajtoch UTF-8. Pri kódovaní do URL môže každý bajt narásť na 3 znaky (diakritika
 * ~6× na znak), takže 2000 bajtov dá najviac 6000 znakov — pod 8 KB limitom riadku požiadavky v nginx.
 */
export const ADVANCED_FILTER_MAX_RAW = 2000

export function isAdvancedFilterTooLong(raw: string): boolean {
  // Bez TextEncoder: balík nemá DOM ani Node typy.
  let bytes = 0
  for (const ch of raw) {
    const cp = ch.codePointAt(0) ?? 0
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4
    if (bytes > ADVANCED_FILTER_MAX_RAW) return true
  }
  return false
}

export const ADVANCED_OPERATORS = ['contains', 'notContains', 'eq', 'neq', 'empty', 'notEmpty', 'gte', 'lte'] as const
export type AdvancedOperator = (typeof ADVANCED_OPERATORS)[number]

export const ADVANCED_JOINS = ['and', 'andNot', 'or'] as const
export type AdvancedJoin = (typeof ADVANCED_JOINS)[number]
export const ADVANCED_JOIN_LABELS: Record<AdvancedJoin, string> = { and: 'a', andNot: 'a nie', or: 'alebo' }

export type AdvancedFieldType = 'text' | 'phone' | 'select' | 'agent' | 'tiper' | 'date' | 'bool' | 'budget' | 'sensitive'

export interface AdvancedOption {
  value: string
  label: string
  /** Skupina vo výbere (okres → kraj). */
  group?: string
}

export interface ContactAdvancedFieldDef {
  key: string
  label: string
  /** Skupina vo výbere poľa. */
  group: string
  type: AdvancedFieldType
  /** Vždy vyplnené pole — bez „je prázdne" / „nie je prázdne". */
  required?: boolean
  /** Pole záujmu: podmienku stačí, keď ju spĺňa aspoň jeden aktívny záujem osoby. */
  interest?: boolean
  options?: readonly AdvancedOption[]
}

const fromLabels = (labels: Record<string, string>): AdvancedOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }))

const DISTRICT_OPTIONS: AdvancedOption[] = SLOVAK_REGIONS.flatMap((region) =>
  REGION_DISTRICTS[region].map((district) => ({ value: district, label: district, group: REGION_LABELS[region] })),
)

const PERSON = 'Osoba'
const SENSITIVE = 'Citlivé údaje'
const CRM = 'CRM'
const DATES = 'Dátumy a súhlasy'
const INTEREST = 'Záujem'

/** Polia v poradí výberu; názvy ako vo formulári kontaktu. */
export const CONTACT_ADVANCED_FIELDS = [
  { key: 'firstName', label: 'Meno', group: PERSON, type: 'text' },
  { key: 'lastName', label: 'Priezvisko', group: PERSON, type: 'text' },
  { key: 'birthSurname', label: 'Rodné priezvisko', group: PERSON, type: 'text' },
  { key: 'email', label: 'E-mail', group: PERSON, type: 'text' },
  { key: 'phone', label: 'Telefón', group: PERSON, type: 'phone' },
  { key: 'company', label: 'Firma', group: PERSON, type: 'text' },
  { key: 'isCompany', label: 'Je firma', group: PERSON, type: 'bool' },
  { key: 'ico', label: 'IČO', group: PERSON, type: 'text' },
  { key: 'dic', label: 'DIČ', group: PERSON, type: 'text' },
  { key: 'icDph', label: 'IČ DPH', group: PERSON, type: 'text' },
  { key: 'address', label: 'Adresa', group: PERSON, type: 'text' },
  { key: 'city', label: 'Obec', group: PERSON, type: 'text' },
  { key: 'zip', label: 'PSČ', group: PERSON, type: 'text' },
  { key: 'maritalStatus', label: 'Rodinný stav', group: PERSON, type: 'select', required: true, options: fromLabels(MARITAL_STATUS_LABELS) },
  { key: 'description', label: 'Poznámka', group: PERSON, type: 'text' },
  { key: 'personalNumber', label: 'Rodné číslo', group: SENSITIVE, type: 'sensitive' },
  { key: 'idNumber', label: 'Číslo dokladu', group: SENSITIVE, type: 'sensitive' },
  { key: 'dateOfBirth', label: 'Dátum narodenia', group: SENSITIVE, type: 'sensitive' },
  { key: 'iban', label: 'IBAN', group: SENSITIVE, type: 'sensitive' },
  { key: 'status', label: 'Stav', group: CRM, type: 'select', required: true, options: fromLabels(LEAD_STATUS_LABELS) },
  { key: 'contactType', label: 'Typ kontaktu', group: CRM, type: 'select', required: true, options: CONTACT_TYPE_OPTIONS },
  { key: 'customerType', label: 'Typ zákazníka', group: CRM, type: 'select', options: CUSTOMER_TYPE_OPTIONS },
  { key: 'source', label: 'Zdroj', group: CRM, type: 'select', options: fromLabels(LEAD_SOURCE_LABELS) },
  { key: 'fromMonitor', label: 'Z Monitora', group: CRM, type: 'bool' },
  { key: 'agent', label: 'Maklér', group: CRM, type: 'agent' },
  { key: 'tiper', label: 'Tiper', group: CRM, type: 'tiper' },
  { key: 'createdAt', label: 'Pridaný', group: DATES, type: 'date', required: true },
  { key: 'contactedAt', label: 'Kontaktovaný', group: DATES, type: 'date' },
  { key: 'firstContactedAt', label: 'Prvý kontakt', group: DATES, type: 'date' },
  { key: 'clientSince', label: 'Zákazník od', group: DATES, type: 'date' },
  { key: 'gdprConsentAt', label: 'Súhlas GDPR', group: DATES, type: 'date' },
  { key: 'marketingConsent', label: 'Marketingový súhlas', group: DATES, type: 'bool' },
  { key: 'interest.kind', label: 'Typ transakcie', group: INTEREST, type: 'select', interest: true, options: TRANSACTION_KIND_OPTIONS },
  {
    key: 'interest.propertyType', label: 'Typ nehnuteľnosti', group: INTEREST, type: 'select', interest: true,
    options: PROPERTY_TYPES.map((value) => ({ value, label: PROPERTY_TYPE_LABELS[value] ?? value })),
  },
  {
    key: 'interest.propertySubtype', label: 'Druh', group: INTEREST, type: 'select', interest: true,
    // Aj vyradené druhy: staršie záujmy ich môžu mať uložené a filter ich má nájsť.
    options: subtypesForGroups(PROPERTY_TYPES, { includeDeprecated: true })
      .map((value) => ({ value, label: PROPERTY_SUBTYPE_LABELS[value] ?? value })),
  },
  {
    key: 'interest.region', label: 'Kraj', group: INTEREST, type: 'select', interest: true,
    options: SLOVAK_REGIONS.map((value) => ({ value, label: REGION_LABELS[value] })),
  },
  { key: 'interest.district', label: 'Okres', group: INTEREST, type: 'select', interest: true, options: DISTRICT_OPTIONS },
  { key: 'interest.budget', label: 'Rozpočet', group: INTEREST, type: 'budget', interest: true },
  { key: 'interest.author', label: 'Záujem pridal', group: INTEREST, type: 'agent', interest: true },
  { key: 'interest.createdAt', label: 'Záujem pridaný', group: INTEREST, type: 'date', interest: true },
] as const satisfies readonly ContactAdvancedFieldDef[]

export type ContactAdvancedFieldKey = (typeof CONTACT_ADVANCED_FIELDS)[number]['key']
export type ContactAdvancedField = ContactAdvancedFieldDef & { key: ContactAdvancedFieldKey }

const FIELD_BY_KEY = new Map<string, ContactAdvancedField>(
  CONTACT_ADVANCED_FIELDS.map((f): [string, ContactAdvancedField] => [f.key, f]),
)

export function contactAdvancedField(key: string): ContactAdvancedField | undefined {
  return FIELD_BY_KEY.get(key)
}

const TEXT_OPERATORS: readonly AdvancedOperator[] = ['contains', 'notContains', 'eq', 'neq', 'empty', 'notEmpty']
const CHOICE_OPERATORS: readonly AdvancedOperator[] = ['eq', 'neq', 'empty', 'notEmpty']
const OPERATORS_BY_TYPE: Record<AdvancedFieldType, readonly AdvancedOperator[]> = {
  text: TEXT_OPERATORS,
  phone: TEXT_OPERATORS,
  select: CHOICE_OPERATORS,
  agent: CHOICE_OPERATORS,
  tiper: CHOICE_OPERATORS,
  date: ['eq', 'neq', 'gte', 'lte', 'empty', 'notEmpty'],
  bool: ['eq'],
  budget: ['eq', 'gte', 'lte', 'empty', 'notEmpty'],
  // Citlivé údaje: zistiť, komu chýbajú do zmluvy — nie hľadať ľudí podľa rodného čísla.
  sensitive: ['empty', 'notEmpty'],
}

export function isValuelessOperator(op: AdvancedOperator): boolean {
  return op === 'empty' || op === 'notEmpty'
}

/** Operátory poľa v poradí výberu; povinné pole nemá „je prázdne" / „nie je prázdne". */
export function advancedOperatorsFor(field: ContactAdvancedFieldDef): AdvancedOperator[] {
  const ops = OPERATORS_BY_TYPE[field.type]
  return field.required ? ops.filter((op) => !isValuelessOperator(op)) : [...ops]
}

const OPERATOR_LABELS: Record<AdvancedOperator, string> = {
  contains: 'obsahuje',
  notContains: 'neobsahuje',
  eq: 'rovná sa',
  neq: 'nerovná sa',
  empty: 'je prázdne',
  notEmpty: 'nie je prázdne',
  gte: 'od',
  lte: 'do',
}
const DATE_OPERATOR_LABELS: Partial<Record<AdvancedOperator, string>> = { eq: 'v deň', neq: 'nie v deň' }

export function advancedOperatorLabel(field: ContactAdvancedFieldDef, op: AdvancedOperator): string {
  return (field.type === 'date' ? DATE_OPERATOR_LABELS[op] : undefined) ?? OPERATOR_LABELS[op]
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/
const WHOLE_EUROS = /^\d{1,12}$/

/** „RRRR-MM-DD", ktorý existuje (30. 2. neprejde). */
function isIsoDay(value: string): boolean {
  const m = ISO_DAY.exec(value)
  if (!m) return false
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toISOString().startsWith(value)
}

// Postgres odmietne NUL v textovom parametri (500); ostatné riadiace znaky nemajú vo filtri zmysel.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/

/** Chyba hodnoty podmienky (text pre usera), alebo null. Volá sa len pri operátoroch s hodnotou. */
export function advancedValueError(field: ContactAdvancedFieldDef, value: string): string | null {
  const v = value.trim()
  if (!v) return 'Doplňte hodnotu'
  if (CONTROL_CHARS.test(v)) return 'Neplatné znaky'
  switch (field.type) {
    case 'text':
      return v.length > ADVANCED_FILTER_MAX_TEXT ? `Najviac ${ADVANCED_FILTER_MAX_TEXT} znakov` : null
    case 'phone':
      if (v.length > ADVANCED_FILTER_MAX_TEXT) return `Najviac ${ADVANCED_FILTER_MAX_TEXT} znakov`
      return normalizePhoneSearch(v) ? null : `Zadajte aspoň ${SEARCH_MIN_LENGTH} číslice telefónu`
    case 'select':
      return field.options?.some((o) => o.value === v) ? null : 'Vyberte hodnotu'
    case 'agent':
    case 'tiper':
      return UUID.test(v) ? null : 'Vyberte hodnotu'
    case 'date':
      return isIsoDay(v) ? null : 'Zadajte dátum'
    case 'bool':
      return v === 'true' || v === 'false' ? null : 'Vyberte hodnotu'
    case 'budget':
      return WHOLE_EUROS.test(v) ? null : 'Zadajte sumu v celých eurách'
    case 'sensitive':
      return 'Citlivé údaje sa filtrujú len podľa toho, či sú vyplnené'
  }
}

export interface ContactAdvancedCondition {
  /** Spojka s predchádzajúcou podmienkou; prvá podmienka ju nemá. */
  join?: AdvancedJoin
  field: ContactAdvancedFieldKey
  op: AdvancedOperator
  /** Pri „je prázdne" / „nie je prázdne" chýba. */
  value?: string
}

export type ContactAdvancedFilterParse =
  | { ok: true; conditions: ContactAdvancedCondition[] }
  | { ok: false; error: string }

const isJoin = (j: unknown): j is AdvancedJoin => typeof j === 'string' && (ADVANCED_JOINS as readonly string[]).includes(j)

/** Položka z URL (`j` spojka, `f` pole, `o` operátor, `v` hodnota) → podmienka, alebo text chyby. */
function parseCondition(item: unknown, isFirst: boolean): ContactAdvancedCondition | string {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return 'nie je objekt'
  const { j, f, o, v } = item as Record<string, unknown>
  if (isFirst ? j !== undefined : !isJoin(j)) return isFirst ? 'prvá podmienka nemá spojku' : 'chýba platná spojka'
  const field = typeof f === 'string' ? contactAdvancedField(f) : undefined
  if (!field) return 'neznáme pole'
  const op = advancedOperatorsFor(field).find((candidate) => candidate === o)
  if (!op) return 'operátor nie je pre pole povolený'
  const base: ContactAdvancedCondition = { ...(isJoin(j) && { join: j }), field: field.key, op }
  if (isValuelessOperator(op)) return v === undefined ? base : 'operátor nemá hodnotu'
  if (v === undefined || v === null) return 'chýba hodnota'
  if (typeof v !== 'string') return 'hodnota nie je text'
  return advancedValueError(field, v) ?? { ...base, value: v.trim() }
}

/** `adv` z URL → podmienky. Admin aj API prijmú len to, čo prejde touto kontrolou. */
export function parseContactAdvancedFilter(raw: string): ContactAdvancedFilterParse {
  if (isAdvancedFilterTooLong(raw)) return { ok: false, error: 'filter je príliš dlhý' }
  let items: unknown
  try {
    items = JSON.parse(raw)
  } catch {
    return { ok: false, error: 'nie je JSON' }
  }
  if (!Array.isArray(items)) return { ok: false, error: 'filter nie je zoznam podmienok' }
  if (items.length === 0) return { ok: false, error: 'filter nemá podmienky' }
  if (items.length > ADVANCED_FILTER_MAX_CONDITIONS) {
    return { ok: false, error: `viac ako ${ADVANCED_FILTER_MAX_CONDITIONS} podmienok` }
  }
  const conditions: ContactAdvancedCondition[] = []
  for (const [i, item] of items.entries()) {
    const parsed = parseCondition(item, i === 0)
    if (typeof parsed === 'string') return { ok: false, error: `podmienka ${i + 1}: ${parsed}` }
    conditions.push(parsed)
  }
  return { ok: true, conditions }
}

/** Podmienky → `adv` (krátke kľúče, aby bola URL krátka). */
export function serializeContactAdvancedFilter(conditions: readonly ContactAdvancedCondition[]): string {
  return JSON.stringify(
    conditions.map((c) => ({
      ...(c.join && { j: c.join }),
      f: c.field,
      o: c.op,
      ...(c.value !== undefined && !isValuelessOperator(c.op) && { v: c.value }),
    })),
  )
}

/** Skupiny podmienok: „alebo" začína novú skupinu, v skupine platí a / a nie (ako AND pred OR v SQL). */
export function groupAdvancedConditions<T extends { join?: AdvancedJoin }>(conditions: readonly T[]): T[][] {
  const groups: T[][] = []
  for (const condition of conditions) {
    const current = groups.at(-1)
    if (!current || condition.join === 'or') groups.push([condition])
    else current.push(condition)
  }
  return groups
}
